import type { QueryClient } from '@tanstack/react-query';
import { endOfToday } from 'date-fns';

import { getLogger } from '@/lib/logger';

/**
 * 今日の残り時間をミリ秒で返す（0 未満にはならない）
 *
 * `date-fns` の `endOfToday()` により、**ブラウザのローカルタイムゾーン**における
 * 当日 23:59:59.999 までの残り時間を返す。組織タイムゾーンやユーザー設定と
 * 一致しない場合がある。
 *
 * `useQuery` の `staleTime` に渡すことで、当日中はキャッシュを使い続ける。
 * @example
 * // 現在時刻が 2022-01-01T12:00:00 の場合
 * remainingTimeToday() // 43200000 (12時間)
 */
export function remainingTimeToday() {
  return Math.max(0, endOfToday().getTime() - Date.now());
}

/**
 * 5分間の時間
 * @note 5分間の時間は、`useQuery` の `staleTime` に渡すことを想定
 */
export const FIVE_MINUTES = 1000 * 60 * 5;

/**
 * 30分間の時間（ミリ秒）
 * @note `useQuery` の `gcTime` などに渡すことを想定
 */
export const THIRTY_MINUTES = 1000 * 60 * 30;

/**
 * TanStack Query キャッシュプリセット
 *
 * データの性質ごとに staleTime / gcTime / refetchOnWindowFocus を組み合わせた既定値を提供する。
 * 新規クエリは原則どれかのプリセットに当てる。すべて無引数関数として呼び出す
 * （`...QUERY_PRESETS.xxx()`）。詳細は `dev-query-cache` スキルを参照。
 *
 *
 * @example
 * useQuery({ ...orpc.workforce.list.queryOptions(p), ...QUERY_PRESETS.list() });
 * useQuery({ ...orpc.software.overviewTenantPeople.queryOptions({ input: {} }), ...QUERY_PRESETS.daily() });
 */
export const QUERY_PRESETS = {
  /**
   * 一覧・サマリー・参照系全般（鮮度優先）
   *
   * 用途: 複数行データまたは集計値を表示する読み取り画面全般で、
   *       業務再開時に比較的早く最新化したいもの。
   *       （一覧テーブル、アラート、KPI サマリー、アクティビティログ等）
   * 他ユーザーが裏で更新する前提。タブ復帰時に stale なら refetch される。
   * gcTime > staleTime（公式推奨）。
   *
   * gcTime 設計: 業務利用ではタブ・画面切替が頻発するため、observer 解除後 30 分は
   *     キャッシュを保持し、短時間離席→復帰時の再ローディングちらつきを抑える。
   */
  list: () =>
    ({
      staleTime: FIVE_MINUTES,
      gcTime: THIRTY_MINUTES,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    }) as const,

  /**
   * 当日中フレッシュ扱い（重い集計・ダッシュボード・ほぼ変動しない参照データ）
   *
   * 用途: Software Overview のダッシュボードカード、リソース定義系、
   *       Proxy User / Group ID 等、頻繁な再取得が不要なもの。
   * 当日中はキャッシュをそのまま使い、日跨ぎ後のタブ復帰で refetch される。
   *
   * 注: `refetchOnWindowFocus: true` は stale 時のみ発火する仕様のため、
   *     当日中はタブ復帰だけでは refetch されず、他ユーザー更新も即拾わない（その用途は `list` を使う）。
   *     同日中にバックエンド更新（例: 深夜〜朝のバッチ）を画面に即反映したい場合は `list()` を使うか、
   *     バッチ完了後に `invalidateQueries` する。
   *     `staleTime` / `gcTime` は呼び出し時点の当日残り時間で揃える（gcTime >= staleTime）。
   *     当日中の unmount → remount ではキャッシュを再利用できる。
   *     必ず関数として呼び出すこと（モジュールロード時に値が固定化されないよう）。
   *
   * 呼び出し側: TanStack Query は `staleTime` をクエリ登録時の数値として保持する。
   *     `useQuery({ ..., ...QUERY_PRESETS.daily() })` の options 展開で毎回 `()` を呼ぶこと。
   *     `useMemo(() => QUERY_PRESETS.daily(), [])` は禁止（日跨ぎ後も stale にならない）。
   *
   * トレードオフ: unmount 後も当日中はキャッシュがメモリに残る（`list` の 30 分 GC より長い）。
   *     重いダッシュボード向け。observer マウント中のメモリ増は `list` と同様。
   *     日末付近は gcTime も `remainingTimeToday()` に比例して短くなるため、
   *     深夜の画面遷移では unmount 後のキャッシュ再利用窓が狭い点に注意。
   */
  daily: () => {
    const untilEndOfToday = remainingTimeToday();
    return {
      staleTime: untilEndOfToday,
      gcTime: untilEndOfToday,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    } as const;
  },

  /**
   * 詳細・編集（明示的に設定、TanStack Query v5 デフォルトと一致）
   *
   * 用途: 単一エンティティの参照画面で、直後に編集操作が走る前提のもの。
   * 編集前に最新化したいので、常に stale 扱いで mount / focus / reconnect 時に refetch する。
   */
  detail: () =>
    ({
      staleTime: 0,
      gcTime: FIVE_MINUTES,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    }) as const,

  /**
   * 機微情報（鮮度最大化 + 観測終了直後破棄）
   *
   * 用途: 取り扱いに法的・契約的制約のある機微情報、または認証情報。
   *      （個人識別情報、Credential、Token、機微属性、権限スコープ表示等）
   *
   * 設計思想: **鮮度最大 + 即破棄**。
   *   - 古い権限・失効トークン等で業務判断されないよう、観測中は常に最新を取得する。
   *   - `staleTime: 0` で常に stale 扱い、`refetchOnMount: 'always'` で stale 判定を
   *     スキップして必ず再 fetch、`refetchOnWindowFocus: true` でタブ復帰時も最新化。
   *   - `refetchOnReconnect: true` で再接続時にも最新化。
   *   - `gcTime: 0` により、active observer が消えた直後にキャッシュが破棄される。
   *     画面を閉じた瞬間に機微情報がメモリから除去される（滞留時間最小化）。
   *
   * `detail` との違い: `gcTime: 0`（即破棄）と `refetchOnMount: 'always'`
   *      （stale 判定スキップで必ず再 fetch）の 2 点。
   *
   * 注意: 画面表示中（active observer が存在する間）はキャッシュがメモリに保持される。
   *      `gcTime: 0` が保証するのは observer 解除直後の破棄であり、観測中の滞留は
   *      避けられない。表示時間そのものを最小化する設計（モーダル限定表示等）は
   *      呼び出し側の責務。
   */
  confidential: () =>
    ({
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: 'always',
    }) as const,
} as const;

/**
 * セッション切替（ログアウト・組織切替・sign-in でのセッション無効化）時にキャッシュを完全に破棄する。
 *
 * 進行中のリクエストをキャンセルしてから clear することで、レース条件で前テナントの
 * データが書き戻されることを防ぐ（クロステナント漏洩防止）。
 * 将来テナント切替 UI を実装する場合も、切替 API 成功直後に本ヘルパを呼ぶこと。
 *
 * @see src/apps/app/src/lib/auth/index.ts — ログアウトフローで本ヘルパを使用。
 */
export async function clearAllQueries(queryClient: QueryClient): Promise<void> {
  try {
    await queryClient.cancelQueries();
  } catch (error) {
    getLogger().warn('Failed to cancel queries before cache clear', { error });
    throw error;
  } finally {
    queryClient.clear();
  }
}
